import { useUserInfo } from "@/api/user";
import { PopoverTrigger } from "@/components/ui/popover";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import { HeaderSettingsSlot } from "./HeaderSettingsSlot";
import { HomeClock } from "./HomeClock";
import { SwitcherCaret, WorkspaceSwitcherPopover } from "./WorkspaceSwitcher";

/**
 * Greeting row: Cohesive mark + a personal welcome ("Welcome <first name>", or
 * "Welcome back" before the profile loads or when no name is set) on the left,
 * wall clock on the right. The greeting doubles as the workspace switcher
 * trigger — Home is the checked entry, since `/` is the cross-workspace
 * overview rather than any single workspace (the composer's scope chip is what
 * says where an unscoped chat lands).
 *
 * Structurally mirrors `WorkspaceSurfaceHeader` so the clock lands on the same
 * pixel on both surfaces — hence the empty settings slot below, which home has
 * no control to put in.
 */
export function HomeGreeting() {
  const { data: userInfo } = useUserInfo();
  const firstName = userInfo?.user?.first_name?.trim();
  const greeting = firstName ? `Welcome, ${firstName}` : "Welcome back";

  return (
    <div className="flex w-full items-center justify-between gap-4 select-none">
      <WorkspaceSwitcherPopover>
        <h1 className="min-w-0">
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Switch workspace"
              className="group -ml-1 flex min-w-0 items-center gap-2.5 rounded-lg py-0.5 pl-1 pr-1.5 hover:bg-muted/60 data-[state=open]:bg-muted/60"
            >
              <LogoIcon className="h-[18px] w-[18px] shrink-0" />
              <span className="truncate text-xl font-medium text-n-90">
                {greeting}
              </span>
              <SwitcherCaret />
            </button>
          </PopoverTrigger>
        </h1>
      </WorkspaceSwitcherPopover>
      <div className="flex shrink-0 items-center gap-2">
        {/* Invisible twin of the workspace header's settings button; keeps the
            clock from shifting as you navigate between `/` and a workspace. */}
        <HeaderSettingsSlot />
        <HomeClock />
      </div>
    </div>
  );
}
